import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  type ReactNode,
} from 'react'
import { gql, useApolloClient, useMutation, useQuery } from '@apollo/client'
import type {
  CreateB2bOrderInput,
  FulfillmentLine,
  FulfillmentOrder,
  FulfillmentStatus,
  OrderPaymentStatus,
  OrderPaymentTerms,
  OrderQcChecklist,
  OrderQcItem,
  OrderShipment,
  OrderSource,
  QcCategory,
  QcItemStatus,
} from '@/data/orders/types'

/* ───────────────────────────  GraphQL  ─────────────────────────── */

/* Shared field selection so the list, detail and every mutation return the
   same FulfillmentOrder graph — mutations returning the full order let Apollo's
   normalized cache update the detail view without a manual refetch. */
const ORDER_FIELDS = `
  id
  orderNumber
  externalOrderId
  source
  fulfillmentStatus
  channelStatus
  fulfillmentWarehouseId
  customerName
  customerEmail
  clientLegalName
  doctorName
  gstin
  paymentTerms
  paymentStatus
  amountPaid
  dueDate
  totalAmount
  currency
  discountType
  discountValue
  discountAmount
  contactPerson
  contactMobile
  contactEmail
  specialInstructions
  shippingAddressJson
  billingAddressJson
  backordered
  backorderReason
  invoiceId
  orderDate
  createdAt
  modifiedAt
  lines {
    id
    productId
    externalSku
    title
    quantity
    unitPrice
    lineTotal
  }
  qcChecklist {
    id
    sopReference
    templateVersion
    status
    completedAt
    allMandatoryPassed
    items {
      id
      category
      sequenceNumber
      checkPoint
      acceptanceCriteria
      mandatory
      status
      remarks
      inspectedByName
      inspectedAt
    }
  }
  shipment {
    id
    carrier
    awbNumber
    externalShipmentId
    trackingUrl
    status
    packageCount
    weightGrams
    dispatchedAt
    deliveredAt
  }
`

const FULFILLMENT_ORDERS = gql`
  query FulfillmentOrders($status: FulfillmentStatus, $source: OrderSource, $warehouseId: ID, $page: Int, $size: Int) {
    fulfillmentOrders(status: $status, source: $source, warehouseId: $warehouseId, page: $page, size: $size) {
      content { ${ORDER_FIELDS} }
      pageInfo { page size totalElements totalPages }
    }
  }
`

const FULFILLMENT_ORDER = gql`
  query FulfillmentOrder($id: ID!) {
    fulfillmentOrder(id: $id) { ${ORDER_FIELDS} }
  }
`

const MY_FULFILLMENT_QUEUE = gql`
  query MyFulfillmentQueue($warehouseId: ID, $page: Int, $size: Int) {
    myFulfillmentQueue(warehouseId: $warehouseId, page: $page, size: $size) {
      content { ${ORDER_FIELDS} }
      pageInfo { page size totalElements totalPages }
    }
  }
`

const CREATE_B2B_ORDER = gql`
  mutation CreateB2bOrder($input: CreateB2bOrderInput!) {
    createB2bOrder(input: $input) { ${ORDER_FIELDS} }
  }
`

const CONFIRM_B2B_ORDER = gql`
  mutation ConfirmB2bOrder($orderId: ID!) {
    confirmB2bOrder(orderId: $orderId) { ${ORDER_FIELDS} }
  }
`

const RECORD_ORDER_QC_ITEM = gql`
  mutation RecordOrderQcItem($orderId: ID!, $itemId: ID!, $status: QcItemStatus!, $remarks: String) {
    recordOrderQcItem(orderId: $orderId, itemId: $itemId, status: $status, remarks: $remarks) { ${ORDER_FIELDS} }
  }
`

const MARK_PACKED = gql`
  mutation MarkPacked($orderId: ID!) {
    markPacked(orderId: $orderId) { ${ORDER_FIELDS} }
  }
`

const MARK_READY_TO_DISPATCH = gql`
  mutation MarkReadyToDispatch($orderId: ID!) {
    markReadyToDispatch(orderId: $orderId) { ${ORDER_FIELDS} }
  }
`

const ATTACH_SHIPMENT = gql`
  mutation AttachShipment($orderId: ID!, $carrier: String!, $awbNumber: String!, $trackingUrl: String) {
    attachShipment(orderId: $orderId, carrier: $carrier, awbNumber: $awbNumber, trackingUrl: $trackingUrl) { ${ORDER_FIELDS} }
  }
`

const MARK_SHIPPED = gql`
  mutation MarkShipped($orderId: ID!) {
    markShipped(orderId: $orderId) { ${ORDER_FIELDS} }
  }
`

const MARK_DELIVERED = gql`
  mutation MarkDelivered($orderId: ID!) {
    markDelivered(orderId: $orderId) { ${ORDER_FIELDS} }
  }
`

const CANCEL_ORDER = gql`
  mutation CancelOrder($orderId: ID!, $reason: String) {
    cancelOrder(orderId: $orderId, reason: $reason) { ${ORDER_FIELDS} }
  }
`

const RECORD_ORDER_PAYMENT = gql`
  mutation RecordOrderPayment($orderId: ID!, $amount: Float!) {
    recordOrderPayment(orderId: $orderId, amount: $amount) { ${ORDER_FIELDS} }
  }
`

/* Active list/queue operation names — refetched after any status-changing mutation
   so an order moving between statuses drops out of / into filtered lists. */
const LIST_OPERATIONS = ['FulfillmentOrders', 'MyFulfillmentQueue']

/* ───────────────────────────  Raw GQL shapes  ─────────────────────────── */

interface GqlLine {
  id: string
  productId?: string | null
  externalSku?: string | null
  title?: string | null
  quantity?: number | string | null
  unitPrice?: number | string | null
  lineTotal?: number | string | null
}

interface GqlQcItem {
  id: string
  category: QcCategory
  sequenceNumber?: number | null
  checkPoint?: string | null
  acceptanceCriteria?: string | null
  mandatory?: boolean | null
  status: QcItemStatus
  remarks?: string | null
  inspectedByName?: string | null
  inspectedAt?: string | null
}

interface GqlQcChecklist {
  id: string
  sopReference?: string | null
  templateVersion?: string | null
  status?: string | null
  completedAt?: string | null
  allMandatoryPassed?: boolean | null
  items?: GqlQcItem[] | null
}

interface GqlShipment {
  id: string
  carrier?: string | null
  awbNumber?: string | null
  externalShipmentId?: string | null
  trackingUrl?: string | null
  status?: string | null
  packageCount?: number | null
  weightGrams?: number | null
  dispatchedAt?: string | null
  deliveredAt?: string | null
}

interface GqlOrder {
  id: string
  orderNumber: string
  externalOrderId?: string | null
  source: OrderSource
  fulfillmentStatus: FulfillmentStatus
  channelStatus?: string | null
  fulfillmentWarehouseId?: string | null
  customerName?: string | null
  customerEmail?: string | null
  clientLegalName?: string | null
  doctorName?: string | null
  gstin?: string | null
  paymentTerms?: OrderPaymentTerms | null
  paymentStatus?: OrderPaymentStatus | null
  amountPaid?: number | string | null
  dueDate?: string | null
  totalAmount?: number | string | null
  currency?: string | null
  discountType?: string | null
  discountValue?: number | string | null
  discountAmount?: number | string | null
  contactPerson?: string | null
  contactMobile?: string | null
  contactEmail?: string | null
  specialInstructions?: string | null
  shippingAddressJson?: string | null
  billingAddressJson?: string | null
  backordered?: boolean | null
  backorderReason?: string | null
  invoiceId?: string | null
  orderDate?: string | null
  createdAt?: string | null
  modifiedAt?: string | null
  lines?: GqlLine[] | null
  qcChecklist?: GqlQcChecklist | null
  shipment?: GqlShipment | null
}

interface GqlConnection {
  content?: GqlOrder[] | null
  pageInfo?: PageInfo | null
}

export interface PageInfo {
  page: number
  size: number
  totalElements: number
  totalPages: number
}

/* ───────────────────────────  Mappers  ─────────────────────────── */

const num = (v: number | string | null | undefined): number => (v == null ? 0 : Number(v) || 0)
const numOrNull = (v: number | string | null | undefined): number | null =>
  v == null ? null : Number(v) || 0

function mapLine(l: GqlLine): FulfillmentLine {
  return {
    id: l.id,
    productId: l.productId ?? null,
    externalSku: l.externalSku ?? null,
    title: l.title ?? 'Item',
    quantity: num(l.quantity),
    unitPrice: num(l.unitPrice),
    lineTotal: num(l.lineTotal),
  }
}

function mapQcItem(i: GqlQcItem): OrderQcItem {
  return {
    id: i.id,
    category: i.category,
    sequenceNumber: i.sequenceNumber ?? 0,
    checkPoint: i.checkPoint ?? '',
    acceptanceCriteria: i.acceptanceCriteria ?? null,
    mandatory: i.mandatory ?? false,
    status: i.status,
    remarks: i.remarks ?? null,
    inspectedByName: i.inspectedByName ?? null,
    inspectedAt: i.inspectedAt ?? null,
  }
}

function mapChecklist(c?: GqlQcChecklist | null): OrderQcChecklist | null {
  if (!c) return null
  return {
    id: c.id,
    sopReference: c.sopReference ?? null,
    templateVersion: c.templateVersion ?? null,
    status: c.status ?? '',
    completedAt: c.completedAt ?? null,
    allMandatoryPassed: c.allMandatoryPassed ?? false,
    items: (c.items ?? []).map(mapQcItem),
  }
}

function mapShipment(s?: GqlShipment | null): OrderShipment | null {
  if (!s) return null
  return {
    id: s.id,
    carrier: s.carrier ?? null,
    awbNumber: s.awbNumber ?? null,
    externalShipmentId: s.externalShipmentId ?? null,
    trackingUrl: s.trackingUrl ?? null,
    status: s.status ?? null,
    packageCount: s.packageCount ?? null,
    weightGrams: s.weightGrams ?? null,
    dispatchedAt: s.dispatchedAt ?? null,
    deliveredAt: s.deliveredAt ?? null,
  }
}

export function mapOrder(o: GqlOrder): FulfillmentOrder {
  return {
    id: o.id,
    orderNumber: o.orderNumber,
    externalOrderId: o.externalOrderId ?? null,
    source: o.source,
    fulfillmentStatus: o.fulfillmentStatus,
    channelStatus: o.channelStatus ?? null,
    fulfillmentWarehouseId: o.fulfillmentWarehouseId ?? null,
    customerName: o.customerName ?? null,
    customerEmail: o.customerEmail ?? null,
    clientLegalName: o.clientLegalName ?? null,
    doctorName: o.doctorName ?? null,
    gstin: o.gstin ?? null,
    paymentTerms: o.paymentTerms ?? null,
    paymentStatus: o.paymentStatus ?? null,
    amountPaid: numOrNull(o.amountPaid),
    dueDate: o.dueDate ?? null,
    totalAmount: num(o.totalAmount),
    currency: o.currency ?? 'INR',
    discountType: o.discountType ?? null,
    discountValue: numOrNull(o.discountValue),
    discountAmount: numOrNull(o.discountAmount),
    contactPerson: o.contactPerson ?? null,
    contactMobile: o.contactMobile ?? null,
    contactEmail: o.contactEmail ?? null,
    specialInstructions: o.specialInstructions ?? null,
    shippingAddressJson: o.shippingAddressJson ?? null,
    billingAddressJson: o.billingAddressJson ?? null,
    backordered: o.backordered ?? null,
    backorderReason: o.backorderReason ?? null,
    invoiceId: o.invoiceId ?? null,
    orderDate: o.orderDate ?? null,
    createdAt: o.createdAt ?? null,
    modifiedAt: o.modifiedAt ?? null,
    lines: (o.lines ?? []).map(mapLine),
    qcChecklist: mapChecklist(o.qcChecklist),
    shipment: mapShipment(o.shipment),
  }
}

/* ───────────────────────────  Query hooks  ─────────────────────────── */

export interface FulfillmentOrdersFilter {
  status?: FulfillmentStatus
  source?: OrderSource
  warehouseId?: string
  page?: number
  size?: number
}

/** Live list of fulfilment orders (optionally filtered by status/source/warehouse). */
export function useFulfillmentOrders(filter: FulfillmentOrdersFilter = {}) {
  const { data, loading, error, refetch } = useQuery(FULFILLMENT_ORDERS, {
    variables: {
      status: filter.status,
      source: filter.source,
      warehouseId: filter.warehouseId,
      page: filter.page ?? 0,
      size: filter.size ?? 100,
    },
  })
  const conn: GqlConnection | undefined = data?.fulfillmentOrders
  const orders = useMemo(() => (conn?.content ?? []).map(mapOrder), [conn])
  return { orders, pageInfo: conn?.pageInfo ?? null, loading, error, refetch }
}

/** Single order by id (skipped when no id). */
export function useFulfillmentOrder(id: string | undefined) {
  const { data, loading, error, refetch } = useQuery(FULFILLMENT_ORDER, {
    variables: { id },
    skip: !id,
  })
  const order = useMemo(
    () => (data?.fulfillmentOrder ? mapOrder(data.fulfillmentOrder as GqlOrder) : null),
    [data],
  )
  return { order, loading, error, refetch }
}

/** The signed-in user's warehouse fulfilment queue. */
export function useMyQueue(filter: { warehouseId?: string; page?: number; size?: number } = {}) {
  const { data, loading, error, refetch } = useQuery(MY_FULFILLMENT_QUEUE, {
    variables: {
      warehouseId: filter.warehouseId,
      page: filter.page ?? 0,
      size: filter.size ?? 100,
    },
  })
  const conn: GqlConnection | undefined = data?.myFulfillmentQueue
  const orders = useMemo(() => (conn?.content ?? []).map(mapOrder), [conn])
  return { orders, pageInfo: conn?.pageInfo ?? null, loading, error, refetch }
}

/* ───────────────────────────  Context (actions)  ─────────────────────────── */

interface OrdersContextValue {
  createB2bOrder: (input: CreateB2bOrderInput) => Promise<FulfillmentOrder>
  confirmB2bOrder: (orderId: string) => Promise<FulfillmentOrder>
  recordQcItem: (
    orderId: string,
    itemId: string,
    status: QcItemStatus,
    remarks?: string,
  ) => Promise<FulfillmentOrder>
  markPacked: (orderId: string) => Promise<FulfillmentOrder>
  markReadyToDispatch: (orderId: string) => Promise<FulfillmentOrder>
  attachShipment: (
    orderId: string,
    carrier: string,
    awbNumber: string,
    trackingUrl?: string,
  ) => Promise<FulfillmentOrder>
  markShipped: (orderId: string) => Promise<FulfillmentOrder>
  markDelivered: (orderId: string) => Promise<FulfillmentOrder>
  cancelOrder: (orderId: string, reason?: string) => Promise<FulfillmentOrder>
  recordPayment: (orderId: string, amount: number) => Promise<FulfillmentOrder>
}

const OrdersContext = createContext<OrdersContextValue | null>(null)

export function OrdersProvider({ children }: { children: ReactNode }) {
  const client = useApolloClient()

  const [createB2bOrderMut] = useMutation(CREATE_B2B_ORDER)
  const [confirmB2bOrderMut] = useMutation(CONFIRM_B2B_ORDER)
  const [recordQcItemMut] = useMutation(RECORD_ORDER_QC_ITEM)
  const [markPackedMut] = useMutation(MARK_PACKED)
  const [markReadyMut] = useMutation(MARK_READY_TO_DISPATCH)
  const [attachShipmentMut] = useMutation(ATTACH_SHIPMENT)
  const [markShippedMut] = useMutation(MARK_SHIPPED)
  const [markDeliveredMut] = useMutation(MARK_DELIVERED)
  const [cancelOrderMut] = useMutation(CANCEL_ORDER)
  const [recordPaymentMut] = useMutation(RECORD_ORDER_PAYMENT)

  // Refetch the active list/queue queries. The detail query needs no explicit refetch:
  // each mutation returns the full order, which Apollo writes back into the normalized cache.
  const refetchLists = useCallback(async () => {
    await client.refetchQueries({ include: LIST_OPERATIONS }).catch(() => undefined)
  }, [client])

  const createB2bOrder = useCallback<OrdersContextValue['createB2bOrder']>(
    async (input) => {
      // The Apollo client uses errorPolicy: 'all', so GraphQL errors do NOT reject the
      // promise — they arrive in `errors` with data:null. Surface them so a failed
      // create is never mistaken for success.
      const { data, errors } = await createB2bOrderMut({
        variables: {
          input: {
            clientLegalName: input.clientLegalName.trim(),
            doctorName: input.doctorName?.trim() || undefined,
            gstin: input.gstin?.trim() || undefined,
            currency: input.currency || undefined,
            paymentTerms: input.paymentTerms || undefined,
            discountType: input.discountType || undefined,
            discountValue:
              input.discountValue != null && !Number.isNaN(input.discountValue)
                ? input.discountValue
                : undefined,
            contactPerson: input.contactPerson.trim(),
            contactMobile: input.contactMobile.trim(),
            contactEmail: input.contactEmail?.trim() || undefined,
            shippingAddressJson: input.shippingAddressJson,
            billingAddressJson: input.billingAddressJson || undefined,
            specialInstructions: input.specialInstructions?.trim() || undefined,
            fulfillmentWarehouseId: input.fulfillmentWarehouseId || undefined,
            lines: input.lines.map((l) => ({
              productId: l.productId || undefined,
              sku: l.sku || undefined,
              title: l.title || undefined,
              quantity: l.quantity,
              unitPrice: l.unitPrice != null ? l.unitPrice : undefined,
            })),
          },
        },
      })
      if (errors?.length) throw new Error(errors[0].message)
      const created = data?.createB2bOrder
      if (!created?.id) throw new Error('Order could not be created')
      await refetchLists()
      return mapOrder(created as GqlOrder)
    },
    [createB2bOrderMut, refetchLists],
  )

  // Shared post-processing for a mutation that returns a FulfillmentOrder: surface
  // GraphQL errors (errorPolicy:'all' means they don't reject), then refetch the lists
  // and return the mapped order. The detail view updates via the normalized cache.
  const settle = useCallback(
    async (
      result: {
        data?: Record<string, unknown> | null
        errors?: readonly { message: string }[]
      },
      resultKey: string,
    ): Promise<FulfillmentOrder> => {
      if (result.errors?.length) throw new Error(result.errors[0].message)
      const updated = result.data?.[resultKey] as GqlOrder | undefined
      if (!updated?.id) throw new Error('Action failed')
      await refetchLists()
      return mapOrder(updated)
    },
    [refetchLists],
  )

  const confirmB2bOrder = useCallback<OrdersContextValue['confirmB2bOrder']>(
    async (orderId) => settle(await confirmB2bOrderMut({ variables: { orderId } }), 'confirmB2bOrder'),
    [confirmB2bOrderMut, settle],
  )

  const recordQcItem = useCallback<OrdersContextValue['recordQcItem']>(
    async (orderId, itemId, status, remarks) =>
      settle(
        await recordQcItemMut({
          variables: { orderId, itemId, status, remarks: remarks?.trim() || undefined },
        }),
        'recordOrderQcItem',
      ),
    [recordQcItemMut, settle],
  )

  const markPacked = useCallback<OrdersContextValue['markPacked']>(
    async (orderId) => settle(await markPackedMut({ variables: { orderId } }), 'markPacked'),
    [markPackedMut, settle],
  )

  const markReadyToDispatch = useCallback<OrdersContextValue['markReadyToDispatch']>(
    async (orderId) => settle(await markReadyMut({ variables: { orderId } }), 'markReadyToDispatch'),
    [markReadyMut, settle],
  )

  const attachShipment = useCallback<OrdersContextValue['attachShipment']>(
    async (orderId, carrier, awbNumber, trackingUrl) =>
      settle(
        await attachShipmentMut({
          variables: {
            orderId,
            carrier: carrier.trim(),
            awbNumber: awbNumber.trim(),
            trackingUrl: trackingUrl?.trim() || undefined,
          },
        }),
        'attachShipment',
      ),
    [attachShipmentMut, settle],
  )

  const markShipped = useCallback<OrdersContextValue['markShipped']>(
    async (orderId) => settle(await markShippedMut({ variables: { orderId } }), 'markShipped'),
    [markShippedMut, settle],
  )

  const markDelivered = useCallback<OrdersContextValue['markDelivered']>(
    async (orderId) => settle(await markDeliveredMut({ variables: { orderId } }), 'markDelivered'),
    [markDeliveredMut, settle],
  )

  const cancelOrder = useCallback<OrdersContextValue['cancelOrder']>(
    async (orderId, reason) =>
      settle(
        await cancelOrderMut({ variables: { orderId, reason: reason?.trim() || undefined } }),
        'cancelOrder',
      ),
    [cancelOrderMut, settle],
  )

  const recordPayment = useCallback<OrdersContextValue['recordPayment']>(
    async (orderId, amount) =>
      settle(await recordPaymentMut({ variables: { orderId, amount } }), 'recordOrderPayment'),
    [recordPaymentMut, settle],
  )

  const value = useMemo<OrdersContextValue>(
    () => ({
      createB2bOrder,
      confirmB2bOrder,
      recordQcItem,
      markPacked,
      markReadyToDispatch,
      attachShipment,
      markShipped,
      markDelivered,
      cancelOrder,
      recordPayment,
    }),
    [
      createB2bOrder,
      confirmB2bOrder,
      recordQcItem,
      markPacked,
      markReadyToDispatch,
      attachShipment,
      markShipped,
      markDelivered,
      cancelOrder,
      recordPayment,
    ],
  )

  return <OrdersContext.Provider value={value}>{children}</OrdersContext.Provider>
}

export function useOrders(): OrdersContextValue {
  const ctx = useContext(OrdersContext)
  if (!ctx) throw new Error('useOrders must be used within OrdersProvider')
  return ctx
}
